// HelpImageView.jsx

import React, { useEffect, useRef, useState } from 'react';

// Utilities
import {
    IMAGE_RESIZE_HANDLES,
    MIN_HELP_IMAGE_WIDTH,
    nextHelpImageWidth,
    parseHelpImageWidth,
} from './richTextImageUtils';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Components
import { NodeViewWrapper } from '@tiptap/react';

const isTransientImageSrc = (src) => {
    const value = String(src || '').trim();
    return value.startsWith('blob:') || value.startsWith('data:');
};

/**
 * HelpImageView
 *
 * Resolves authenticated storage paths to signed URLs and lets authors resize
 * inline screenshots while keeping the original aspect ratio.
 *
 * @param {object} props - TipTap node view props.
 * @returns {ReactNode}
 */
const HelpImageView = React.memo(({
    extension,
    node,
    selected,
    updateAttributes,
}) => {
    const storagePath = String(node?.attrs?.storagePath || '').trim();
    const rawSrc = String(node?.attrs?.src || '').trim();
    const alt = String(node?.attrs?.alt || '').trim();
    const storedWidth = parseHelpImageWidth(node?.attrs?.width);
    const isReadOnly = extension?.options?.readOnly === true;

    // ----- STATE ----- //

    const [resolvedSrc, setResolvedSrc] = useState(() => (
        isTransientImageSrc(rawSrc) ? rawSrc : ''
    ));
    const [draftWidth, setDraftWidth] = useState(null);
    const [hovered, setHovered] = useState(false);

    const draftWidthRef = useRef(null);
    const frameRef = useRef(null);
    const sessionRef = useRef(null);

    const appliedWidth = draftWidth ?? storedWidth;
    const showHandles = Boolean(
        !isReadOnly && resolvedSrc && (selected || hovered || draftWidth)
    );

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (isTransientImageSrc(rawSrc)) {
            setResolvedSrc(rawSrc);
            return undefined;
        }

        let cancelled = false;
        const path = storagePath || (!rawSrc.includes('://') ? rawSrc : '');

        if (!path && !rawSrc) {
            setResolvedSrc('');
            return undefined;
        }

        resolveImageUrl({
            url: rawSrc || null,
            storagePath: path || null,
        }).then((url) => {
            if (!cancelled) setResolvedSrc(url || rawSrc || '');
        });

        return () => {
            cancelled = true;
        };
    }, [rawSrc, storagePath]);

    // ----- HANDLERS ----- //

    /**
     * handleResizePointerDown
     *
     * Starts a corner-handle drag and captures the pointer so the node does not move.
     *
     * @param {object} event - Pointer event from a handle.
     * @param {string} edge - Corner handle key.
     */
    const handleResizePointerDown = (event, edge) => {
        event.preventDefault();
        event.stopPropagation();
        if (isReadOnly || event.button !== 0) return;

        const frame = frameRef.current;
        const measured = frame?.getBoundingClientRect().width || MIN_HELP_IMAGE_WIDTH;
        const parentWidth = frame?.parentElement?.clientWidth || measured;
        const startWidth = appliedWidth || Math.round(measured);

        sessionRef.current = {
            edge,
            maxWidth: Math.max(MIN_HELP_IMAGE_WIDTH, Math.round(parentWidth)),
            pointerId: event.pointerId,
            startWidth,
            startX: event.clientX,
        };
        draftWidthRef.current = startWidth;
        setDraftWidth(startWidth);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    /**
     * handleResizePointerMove
     *
     * Updates the live width while a handle is dragged.
     *
     * @param {object} event - Pointer event from a handle.
     */
    const handleResizePointerMove = (event) => {
        const session = sessionRef.current;
        if (!session || event.pointerId !== session.pointerId) return;

        event.preventDefault();
        const nextWidth = nextHelpImageWidth({
            clientX: event.clientX,
            edge: session.edge,
            maxWidth: session.maxWidth,
            startWidth: session.startWidth,
            startX: session.startX,
        });

        draftWidthRef.current = nextWidth;
        setDraftWidth(nextWidth);
    };

    /**
     * handleResizePointerUp
     *
     * Commits the dragged width onto the image node.
     *
     * @param {object} event - Pointer event from a handle.
     */
    const handleResizePointerUp = (event) => {
        const session = sessionRef.current;
        if (!session || event.pointerId !== session.pointerId) return;

        sessionRef.current = null;
        const nextWidth = draftWidthRef.current;
        draftWidthRef.current = null;
        setDraftWidth(null);

        if (nextWidth) {
            updateAttributes({ width: nextWidth });
        }
    };

    /**
     * handleHandleMouseDown
     *
     * Stops ProseMirror from treating a resize as a node drag.
     *
     * @param {object} event - Mouse event from a handle.
     */
    const handleHandleMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    /**
     * handleFrameMouseEnter
     *
     * Shows resize handles while the pointer is over the image.
     */
    const handleFrameMouseEnter = () => {
        if (isReadOnly) return;
        setHovered(true);
    };

    /**
     * handleFrameMouseLeave
     *
     * Hides hover handles when the pointer leaves, unless a drag is in progress.
     */
    const handleFrameMouseLeave = () => {
        if (sessionRef.current) return;
        setHovered(false);
    };

    // ----- RENDER ----- //

    if (!resolvedSrc) {
        return (
            <NodeViewWrapper className="rich-text-editor-image-node">

                {/* IMAGE EMPTY */}
                <div className="rich-text-editor-image-empty">
                    Loading image...
                </div>
            </NodeViewWrapper>
        );
    }

    return (
        <NodeViewWrapper
          className={[
              'rich-text-editor-image-node',
              selected ? 'is-selected' : '',
              draftWidth ? 'is-resizing' : '',
          ].filter(Boolean).join(' ')}
        >

            {/* IMAGE FRAME */}
            <div
              className={`rich-text-editor-image-frame${appliedWidth ? ' has-width' : ''}${showHandles ? ' is-active' : ''}`}
              style={appliedWidth ? { width: `${appliedWidth}px` } : undefined}
              ref={frameRef}
              onMouseEnter={handleFrameMouseEnter}
              onMouseLeave={handleFrameMouseLeave}
            >

                {/* IMAGE */}
                <img
                  className={`rich-text-editor-image${selected ? ' is-selected' : ''}`}
                  alt={alt || 'Article image'}
                  draggable={false}
                  src={resolvedSrc}
                />
                {showHandles ? (
                    <>

                        {/* IMAGE HANDLE */}
                        {IMAGE_RESIZE_HANDLES.map((edge) => (
                            <div
                              key={edge}
                              className={`rich-text-editor-image-handle is-${edge}`}
                              aria-label={`Resize image from the ${edge} corner`}
                              onMouseDown={handleHandleMouseDown}
                              onPointerCancel={handleResizePointerUp}
                              onPointerDown={(event) => handleResizePointerDown(event, edge)}
                              onPointerMove={handleResizePointerMove}
                              onPointerUp={handleResizePointerUp}
                            />
                        ))}
                        {appliedWidth ? (

                            /* IMAGE SIZE */
                            <div className="rich-text-editor-image-size">
                                {`${appliedWidth}px`}
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </NodeViewWrapper>
    );
});

HelpImageView.displayName = 'HelpImageView';

export default HelpImageView;
